import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import ActivityTodoList from "./ActivityTodoList";

const ActivitiesSection = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const autoSlideRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const activityTodos = [
    [{ label: "I drink the sun and never walk. What am I❓", key: "eco-riddle", answer: true, correctAnswers: ["water", "tree", "rivers", "stream", "ocean"] }],
    [
      { label: "🧭 Find one green thing.", key: "nature-green", answer: false },
      { label: "What shape is it?", key: "nature-shape", answer: true },
      { label: "What does it feel like?", key: "nature-feel", answer: true },
    ],
    [
      { label: "👀 Close your eyes for 20 seconds.", key: "sound-close", answer: false },
      { label: "Name three sounds.", key: "sound-name", answer: true },
      { label: "Which are wild?", key: "sound-wild", answer: true },
      { label: "Which are human-made?", key: "sound-human", answer: true },
    ],
  ];

  const activities = [
    {
      icon: "🔍",
      title: "Nature Scout",
      color: "#2E7D32",
      bgColor: "#E8F5E9",
      borderColor: "#2E7D32",
      todos: activityTodos[0],
    },
    {
      icon: "🔊",
      title: "Sound Map",
      color: "#66BB6A",
      bgColor: "#F1F8E9",
      borderColor: "#66BB6A",
      todos: activityTodos[1],
    },
    {
      icon: "💡",
      title: "Eco-Riddle",
      color: "#81C784",
      bgColor: "#E8F5E9",
      borderColor: "#81C784",
      todos: activityTodos[2],
    },
  ];

  // Intersection observer for visibility
  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (currentRef) observer.observe(currentRef);
    return () => currentRef && observer.unobserve(currentRef);
  }, []);

  // Auto-slide every 5 minutes
  useEffect(() => {
    autoSlideRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % activities.length);
    }, 300000);
    return () => clearInterval(autoSlideRef.current);
  }, [activities.length]);

  // Navigation
  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % activities.length);
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + activities.length) % activities.length);

  // --- Swipe functionality ---
  const handleTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const handleTouchMove = (e) => (touchEndX.current = e.touches[0].clientX);
  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 60) nextSlide();
    if (distance < -60) prevSlide();
  };

  return (
    <Wrapper ref={sectionRef} id="bela">
      <Container>
        <Header $isVisible={isVisible}>
          <TaglineWrapper>
            <TaglineIcon>🌱</TaglineIcon>
            <Tagline>Try B.E.L.A. Activities Now</Tagline>
          </TaglineWrapper>
          <Title>
            Pre-Release Activities for <TitleHighlight>B.E.L.A.</TitleHighlight>
          </Title>
          <Subtitle>
            Login-free activities that connect kids with nature and spark curiosity about the world around them.
          </Subtitle>
        </Header>

        {/* --- Carousel --- */}
        <CarouselContainer
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <CarouselTrack $activeIndex={activeIndex}>
            {activities.map((activity, idx) => (
              <CarouselSlide key={activity.title}>
                <ActivityCard
                  $bgColor={activity.bgColor}
                  $borderColor={activity.borderColor}
                >
                  <CardHeader $color={activity.color}>
                    <IconWrapper>
                      <Icon>{activity.icon}</Icon>
                    </IconWrapper>
                    <ActivityTitle $color={activity.color}>{activity.title}</ActivityTitle>
                    <ActivityNumber>Activity {idx + 1}</ActivityNumber>
                  </CardHeader>

                  <CardContent>
                    <ActivityTodoList
                      activityIdx={idx}
                      todos={activity.todos}
                      color={activity.color}
                    />
                  </CardContent>
                </ActivityCard>
              </CarouselSlide>
            ))}
          </CarouselTrack>

          <NavButtonLeft onClick={prevSlide}>‹</NavButtonLeft>
          <NavButtonRight onClick={nextSlide}>›</NavButtonRight>

          <Dots>
            {activities.map((_, i) => (
              <Dot
                key={i}
                $active={i === activeIndex}
                onClick={() => setActiveIndex(i)}
              />
            ))}
          </Dots>
        </CarouselContainer>

        {/* CTA */}
        <CTASection>
          <CTAContent>
            <CTAText>
              <CTATitle>Want More Activities?</CTATitle>
              <CTASubtitle>
                The full B.E.L.A. app is coming soon with dozens of missions, challenges, and rewards!
              </CTASubtitle>
            </CTAText>
            <CTAButton href="#email-signup">
              <span>Get Notified</span>
              <ButtonArrow>→</ButtonArrow>
            </CTAButton>
          </CTAContent>
        </CTASection>
      </Container>
    </Wrapper>
  );
};

/* ---------------- STYLES ---------------- */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.section`
  width: 100vw;
  background: linear-gradient(135deg, #F1F8E9 0%, #E8F5E9 50%, #C8E6C9 100%);
  display: flex;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 80px 24px 60px;

  @media (max-width: 768px) {
    padding: 50px 16px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const TaglineWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 2px solid #4CAF50;
  padding: 10px 18px;
  border-radius: 40px;
`;

const TaglineIcon = styled.div` font-size: 1.3rem; `;
const Tagline = styled.span`
  font-size: 0.9rem;
  color: #4CAF50;
  font-weight: 700;
  text-transform: uppercase;
`;
const Title = styled.h2`
  font-size: 2.2rem;
  font-weight: 800;
  color: #1B5E20;
  margin-top: 18px;
`;
const TitleHighlight = styled.span` color: #4CAF50; `;
const Subtitle = styled.p`
  font-size: 1rem;
  color: #2E7D32;
  max-width: 700px;
  margin: 10px auto 0;
`;

/* --- Carousel --- */
const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  margin: 0 auto;
  max-height: 500px;
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${(props) => `-${props.$activeIndex * 100}%`});
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NavButton = styled.button`
  position: absolute;
  top: 45%;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #4CAF50;
  color: #2E7D32;
  font-size: 2rem;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  cursor: pointer;
  transition: 0.3s;
  z-index: 10;

  &:hover {
    background: #4CAF50;
    color: #fff;
    transform: scale(1.1);
  }
`;
const NavButtonLeft = styled(NavButton)` left: 10px; `;
const NavButtonRight = styled(NavButton)` right: 10px; `;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
  gap: 8px;
`;
const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? "#4CAF50" : "#A5D6A7")};
  cursor: pointer;
  transition: all 0.3s;
`;

/* --- Card --- */
const ActivityCard = styled.div`
  background: ${(p) => p.$bgColor};
  border: 3px solid ${(p) => p.$borderColor};
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
  height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;
const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border-bottom: 2px solid ${(p) => p.$color};
  background: rgba(255, 255, 255, 0.6);
`;
const IconWrapper = styled.div` margin-bottom: 8px; `;
const Icon = styled.div` font-size: 2rem; `;
const ActivityTitle = styled.h3`
  font-size: 1.1rem;
  color: ${(p) => p.$color};
  margin: 0;
`;
const ActivityNumber = styled.span`
  font-size: 0.7rem;
  color: #78909C;
  margin-top: 4px;
`;
const CardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  overflow-y: auto;
`;

/* --- CTA --- */
const CTASection = styled.div`
  margin-top: 60px;
`;
const CTAContent = styled.div`
  background: #ffffffc9;
  border: 2px solid #4CAF50;
  border-radius: 18px;
  padding: 24px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 24px;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.2);
`;
const CTAText = styled.div` flex: 1; `;
const CTATitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 800;
  color: #1B5E20;
  margin: 0;
`;
const CTASubtitle = styled.p`
  font-size: 1rem;
  color: #2E7D32;
  margin: 6px 0 0;
`;
const CTAButton = styled.a`
  background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%);
  color: #fff;
  border-radius: 25px;
  padding: 10px 20px;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: 0.3s;
  &:hover {
    transform: scale(1.05);
    background: linear-gradient(135deg, #388E3C 0%, #2E7D32 100%);
  }
`;
const ButtonArrow = styled.span` font-size: 1.3rem; `;

export default ActivitiesSection;
